/**
 * NextStepSection — One calm, customer-facing "what now?" line.
 *
 * Derived purely from the customer projection (no new backend route).
 * The mapping is intentionally conservative:
 *
 *   • If a brief is sealed but no card has been bridged → "Your concierge
 *     brief is ready — pick a recommendation to start a conversation."
 *   • Else if outstanding balance exists → "A deposit is waiting for you."
 *   • Else if a card has been bridged but no proposal yet → "Vendor
 *     conversation in progress — you'll be notified when they reply."
 *   • Else if a proposal is ready → "Review your proposal."
 *   • Else if everything is booked → "You're all set — we'll check in
 *     before the big day."
 *   • Else if intake-only → "Tell us a little more about your event."
 *
 * Tone: warm, declarative, never urgent. No red dots. No "ACTION
 * REQUIRED". One sentence per state.
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';

function deriveNextStep(projection) {
  if (!projection) return null;
  const stage = projection.lifecycle_stage;
  const rail = projection.brief_rail || {};
  const progress = projection.service_progress || [];
  const payments = projection.payments || {};

  const hasBookedSomething = progress.some((p) => p.state === 'booked');
  const allBooked = progress.length > 0 && progress.every((p) => p.state === 'booked');
  const hasProposalReady = progress.some((p) => p.state === 'proposal_ready');
  const hasInTouch = progress.some((p) => p.state === 'in_touch');
  const balanceDue = Number(payments.balance_due || 0);
  const paidSoFar = Number(payments.paid_so_far || 0);

  // Highest-impact first: a deposit waiting always trumps brief steps.
  if (balanceDue > 0 && paidSoFar === 0) {
    return {
      headline: 'A deposit is waiting',
      body: "Confirm a booking by paying its deposit when you're ready.",
    };
  }
  if (hasProposalReady) {
    return {
      headline: 'A proposal is ready for you',
      body: 'Take a look when you have a moment.',
    };
  }
  if (rail.has_brief && rail.latest_status === 'ready' && (rail.bridged_count || 0) === 0 && !rail.brief_viewed) {
    return {
      headline: 'Your concierge brief is ready',
      body: 'Pick a recommendation to start a conversation with that vendor.',
      kind: 'brief_ready',
    };
  }
  if (hasInTouch) {
    return {
      headline: "Vendor conversations in progress",
      body: "We'll let you know the moment they reply.",
    };
  }
  if (allBooked) {
    return {
      headline: "You're all set",
      body: "We'll check in with you before the big day.",
    };
  }
  if (hasBookedSomething) {
    return {
      headline: "Great — one vendor booked",
      body: "A few categories still need attention. We'll guide you.",
    };
  }
  if (stage === 'concierge_active') {
    return {
      headline: 'Your concierge is putting together a brief',
      body: 'Hang tight — usually a day or two.',
    };
  }
  if (stage === 'intake') {
    return {
      headline: 'Tell us a little more about your event',
      body: 'A few extra details help us recommend the right vendors.',
    };
  }
  return null;
}

export default function NextStepSection({ projection, eventId }) {
  const step = deriveNextStep(projection);
  if (!step) return null;
  // Brief-ready is an actionable step — route into the authenticated
  // in-app Brief Viewer (Phase 9A) rather than a static promise.
  const briefHref = step.kind === 'brief_ready' && eventId ? `/events/${eventId}/brief` : null;
  const inner = (
    <>
      <p className="text-xs uppercase tracking-wide text-violet-500 mb-1.5">
        Next step
      </p>
      <p
        className="text-sm font-semibold text-gray-900 leading-snug flex items-start gap-2"
        data-testid="ops-next-step-headline"
      >
        <span>{step.headline}</span>
        <ArrowRight className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" aria-hidden />
      </p>
      {step.body && (
        <p className="text-xs text-gray-500 mt-1" data-testid="ops-next-step-body">
          {step.body}
        </p>
      )}
    </>
  );
  return (
    <div
      className="rounded-xl bg-gradient-to-br from-violet-50 via-white to-white border border-violet-100 p-4"
      data-testid="ops-next-step"
    >
      {briefHref ? (
        <a
          href={briefHref}
          className="block"
          aria-label="Open your Concierge Brief"
          data-testid="ops-next-step-link"
        >
          {inner}
        </a>
      ) : inner}
    </div>
  );
}
